// utils/aiSearchLimit.js - Track AI search usage with localStorage

const AI_SEARCH_LIMIT = 20;
const STORAGE_KEY = 'aiSearchUsage';

/**
 * Get the current month key (e.g., "2025-01")
 */
const getCurrentMonthKey = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

/**
 * Get current usage data from localStorage
 */
const getUsageData = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) {
      return { month: getCurrentMonthKey(), count: 0 };
    }
    return JSON.parse(data);
  } catch (error) {
    console.error('Error reading AI search usage:', error);
    return { month: getCurrentMonthKey(), count: 0 };
  }
};

/**
 * Save usage data to localStorage
 */
const saveUsageData = (data) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Error saving AI search usage:', error);
  }
};

/**
 * Check if user can perform an AI search
 * @returns {boolean} true if under limit, false if over
 */
export const canUseAISearch = () => {
  const usage = getUsageData();
  const currentMonth = getCurrentMonthKey();
  
  // Reset count if it's a new month
  if (usage.month !== currentMonth) {
    return true;
  }
  
  return usage.count < AI_SEARCH_LIMIT;
};

/**
 * Increment the AI search counter
 * Call this after a successful AI search
 */
export const recordAISearch = () => {
  const currentMonth = getCurrentMonthKey();
  const usage = getUsageData();
  
  // Reset if new month
  if (usage.month !== currentMonth) {
    saveUsageData({ month: currentMonth, count: 1 });
  } else {
    saveUsageData({ month: currentMonth, count: usage.count + 1 });
  }
};

/**
 * Get remaining AI searches for the month
 * @returns {number} remaining searches
 */
export const getRemainingSearches = () => {
  const usage = getUsageData();
  const currentMonth = getCurrentMonthKey();
  
  // New month = full quota
  if (usage.month !== currentMonth) {
    return AI_SEARCH_LIMIT;
  }
  
  return Math.max(0, AI_SEARCH_LIMIT - usage.count);
};

/**
 * Get usage info for display
 * @returns {{ used: number, limit: number, remaining: number }}
 */
export const getUsageInfo = () => {
  const usage = getUsageData();
  const currentMonth = getCurrentMonthKey();
  
  // New month = reset
  if (usage.month !== currentMonth) {
    return { used: 0, limit: AI_SEARCH_LIMIT, remaining: AI_SEARCH_LIMIT };
  }
  
  return {
    used: usage.count,
    limit: AI_SEARCH_LIMIT,
    remaining: Math.max(0, AI_SEARCH_LIMIT - usage.count)
  };
};